<template>
  <div>
    <v-alert v-if="isLoading" type="primary" outlined>{{ $t('general.misc.loading') }}</v-alert>
    <v-alert v-else-if="pages.length === 0" type="primary" outlined>{{ $t('phoenix_bote.no_pages') }}</v-alert>

    <div v-else class="flipbook-wrapper">
      <flipbook
          ref="flipbook"
          class="flipbook"
          :pages="pages"
          :start-page="startPage"
          :single-page="singlePageMode"
          :click-to-zoom="false"
          :drag-to-flip="true"
          :zooms="[1]"
          :n-polygons="4"
          :flip-duration="520"
          @flip-left-end="onFlipEnd"
          @flip-right-end="onFlipEnd"
          v-slot="book"
      >
        <v-row class="controls ma-0 pa-2 align-center">
          <v-btn icon :disabled="!book.canFlipLeft" @click="book.flipLeft">
            <v-icon>mdi-chevron-left</v-icon>
          </v-btn>
          <span class="mx-2">{{ getDisplayPageNumbers(book.page) }} / {{ book.numPages }}</span>
          <v-btn icon :disabled="!book.canFlipRight" @click="book.flipRight">
            <v-icon>mdi-chevron-right</v-icon>
          </v-btn>
        </v-row>
      </flipbook>
    </div>
  </div>
</template>

<script>
import Flipbook from "flipbook-vue";
import DataService from "@/services/data.service";
import MessageService from "@/services/message.service";
import {getImageAspectRatio, optimizeImageForFlipbook} from "@/util/FlipbookImage";

export default {
  name: "PhoenixBote",
  components: {Flipbook},
  data() {
    return {
      isLoading: true,
      pages: [],
      startPage: null,
      sortedPageNumbers: [],
      pageGroupMap: {},
      singlePageMode: true
    }
  },
  watch: {
    '$route.query.pageNumber'(newValue) {
      const requestedBookPage = this.getBookPageFromQueryValue(newValue);
      if (requestedBookPage != null && requestedBookPage !== this.startPage) {
        this.startPage = requestedBookPage;
      }
    }
  },
  methods: {
    optimizePageImage(content, targetAspectRatio) {
      return optimizeImageForFlipbook(content, {
        maxWidth: 1200,
        maxHeight: 1700,
        targetAspectRatio: targetAspectRatio,
        maxLength: 900000
      });
    },
    getBookPageFromQueryValue(queryValue) {
      const requestedPageNumber = Number.parseInt(queryValue, 10);
      if (!Number.isInteger(requestedPageNumber) || requestedPageNumber < 1) return null;
      const idx = this.sortedPageNumbers.findIndex(pageNumber => Number(pageNumber) === requestedPageNumber);
      return idx >= 0 ? idx + 1 : null;
    },
    setRoutePageNumberByBookPage(bookPage) {
      const pageNumber = this.sortedPageNumbers[bookPage - 1];
      if (!pageNumber) return;
      const nextQuery = { ...this.$route.query, pageNumber: String(pageNumber) };
      if (this.$route.query.pageNumber === nextQuery.pageNumber) return;
      this.$router.replace({ query: nextQuery }).catch(() => {});
    },
    onFlipEnd(bookPage) {
      this.setRoutePageNumberByBookPage(bookPage);
      this.updateSinglePageMode(bookPage);
    },
    updateSinglePageMode(bookPage) {
      if (bookPage <= 0 || bookPage > this.sortedPageNumbers.length) return;
      const pageNumber = this.sortedPageNumbers[bookPage - 1];
      const groupId = this.pageGroupMap[pageNumber];
      this.singlePageMode = !groupId;
    },
    togglePageMode() {
      this.singlePageMode = !this.singlePageMode;
      localStorage.setItem('phoenixBoteSinglePageMode', JSON.stringify(this.singlePageMode));
    },
    getDisplayPageNumbers(bookPage) {
      if (bookPage <= 0 || bookPage > this.sortedPageNumbers.length) {
        return bookPage;
      }
      const pageNumber = this.sortedPageNumbers[bookPage - 1];
      const groupId = this.pageGroupMap[pageNumber];
      
      if (!groupId) {
        return pageNumber;
      }
      
      // Finde alle Seiten mit der gleichen groupId
      const groupedPages = this.sortedPageNumbers.filter(pn => this.pageGroupMap[pn] === groupId).sort((a, b) => a - b);
      if (groupedPages.length > 1) {
        return `${groupedPages[0]}-${groupedPages[groupedPages.length - 1]}`;
      }
      return pageNumber;
    }
  },
  async mounted() {
    const pages = await DataService.getPhoenixBotePages(true);
    const sortedPages = pages.sort((a, b) => a.pageNumber - b.pageNumber);
    const sortedPageContent = sortedPages.map(page => page.content);
    this.sortedPageNumbers = sortedPages.map(page => page.pageNumber);

    sortedPages.forEach(page => {
      if (page.groupId) {
        this.pageGroupMap[page.pageNumber] = page.groupId;
      }
    });

    // Gruppiere benachbarte Seiten mit gleicher groupId
    sortedPages.forEach((page, index) => {
      if (page.groupId && index < sortedPages.length - 1) {
        const nextPage = sortedPages[index + 1];
        if (nextPage.groupId === page.groupId) {
          // Beide Seiten gehören zusammen - nicht entfernen, sondern behalten
          this.pageGroupMap[page.pageNumber] = page.groupId;
          this.pageGroupMap[nextPage.pageNumber] = page.groupId;
        }
      }
    });

    const targetAspectRatio = sortedPageContent.length > 0
        ? await getImageAspectRatio(sortedPageContent[0]).catch(() => null)
        : null;
    const optimizedPages = await Promise.allSettled(
        sortedPageContent.map(page => this.optimizePageImage(page, targetAspectRatio))
    );
    this.pages = optimizedPages.map((result, index) => result.status === "fulfilled" ? result.value : sortedPageContent[index]);
    if (optimizedPages.some(result => result.status === "rejected")) {
      MessageService.warn("Einige PhoenixBote-Seiten konnten nicht optimiert werden.");
    }

    const requestedBookPage = this.getBookPageFromQueryValue(this.$route.query.pageNumber);
    this.startPage = requestedBookPage ?? this.sortedPageNumbers.length;
    this.setRoutePageNumberByBookPage(this.startPage);
    this.updateSinglePageMode(this.startPage);
    this.isLoading = false;
  }
}
</script>

<style scoped>
.flipbook-wrapper {
  width: 100%;
  height: calc(100vh - 220px);
  min-height: 520px;
}

.flipbook {
  width: 100%;
  height: 100%;
}

.flipbook /deep/ .page {
  object-fit: contain;
  background: #000;
}

.controls {
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 10;
  justify-content: center;
  background: rgba(0, 0, 0, 0.45);
}
</style>

